import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
} from 'react';

import { cx } from '../internal/cx.js';
import { buildSpacingStyle, pickSpacingProps, type SpacingProps } from '../internal/spacing.js';

export type BoxProps = ComponentPropsWithoutRef<'div'> &
  SpacingProps & {
    /** Polymorphic element tag. */
    as?: ElementType;
    width?: CSSProperties['width'];
    height?: CSSProperties['height'];
  };

/** Generic layout box with spacing props. Requires `@import '@nrx/ui/theme.css'`. */
export const Box = forwardRef<HTMLElement, BoxProps>(function Box(
  {
    as,
    className,
    style,
    width,
    height,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'div';
  const spacingStyle = buildSpacingStyle(
    pickSpacingProps({ p, px, py, pt, pr, pb, pl, m, mx, my, mt, mr, mb, ml }),
  );
  const combinedStyle: CSSProperties = { ...spacingStyle, ...style };
  if (width !== undefined) combinedStyle.width = width;
  if (height !== undefined) combinedStyle.height = height;

  return (
    <Component
      ref={ref as never}
      className={cx('nrx-box', className)}
      style={combinedStyle}
      {...rest}
    />
  );
});
